import { useReducer } from "react";

import { actions } from "./actions";
import { ActionPayload, BaseInitialState, ProgressPointState } from "./types";

const baseInitialState: BaseInitialState = {
  pageIndex: 0,
  progressState: [],
  data: undefined,
};

const trackProgressAndUpdateState = ({
  state,
  index: currentIndex,
}: {
  state: BaseInitialState;
  index: number;
}): BaseInitialState => {
  const updatedProgressState = state.progressState.map((state, index) => {
    if (index === currentIndex) {
      return { ...state, state: ProgressPointState.InProgress };
    }

    if (index < currentIndex) {
      return { ...state, state: ProgressPointState.Complete };
    }

    return { ...state, state: ProgressPointState.NotStarted };
  });

  const freezePageIndex = (index: number): number => {
    return Math.max(0, Math.min(state.progressState.length - 1, index));
  };

  return {
    ...state,
    pageIndex: freezePageIndex(currentIndex),
    progressState: updatedProgressState,
  };
};

const reducer = (
  state: BaseInitialState,
  action: ActionPayload
): BaseInitialState => {
  switch (action.type) {
    case actions.RESET_TO_DEFAULT_DATA:
      return { ...state, pageIndex: 0 };
    case actions.SET_PAGE_INDEX:
      return trackProgressAndUpdateState({ state, index: action.payload });
    case actions.INCREMENT_PAGE_INDEX:
      return trackProgressAndUpdateState({ state, index: state.pageIndex + 1 });
    case actions.DECREMENT_PAGE_INDEX:
      return trackProgressAndUpdateState({ state, index: state.pageIndex - 1 });
    case actions.SET_DATA:
      if (state.data !== undefined) {
        return {
          ...state,
          data: {
            ...state.data,
            ...(typeof action.payload === "object" && action.payload !== null
              ? action.payload
              : {}),
          },
        };
      }
      return state;
    default:
      return state;
  }
};

// <Data extends object> => If data is NOT undefined it MUST be an object/array.
// Data should never be a primitive (string, number, boolean)
// If you need to use primitives in data, create them as object properties i.e. { isActive: boolean }
export const useProgressReducer = <Data extends object>(
  initialState: BaseInitialState & { data?: Data }
) => {
  const getInitialStateData = (): Data | undefined => {
    if (initialState.data === undefined) {
      return undefined;
    }
    return initialState.data;
  };
  const mergedInitialState: BaseInitialState = {
    ...baseInitialState,
    ...initialState,
    data: getInitialStateData(),
  };

  return useReducer(reducer, mergedInitialState);
};
